import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
  Popover,
  FormControlLabel,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { styled, alpha, useTheme, type Theme } from '@mui/material/styles';
import { useAppSelector, useAppDispatch } from 'store/hook';
import { showNotification } from 'store/notification.slice';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import FilterListIcon from '@mui/icons-material/FilterList';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import VpnKeyOutlinedIcon from '@mui/icons-material/VpnKeyOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import AddLinkOutlinedIcon from '@mui/icons-material/AddLinkOutlined';
import LinkOffOutlinedIcon from '@mui/icons-material/LinkOffOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import LoginOutlinedIcon from '@mui/icons-material/LoginOutlined';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import InputAdornment from '@mui/material/InputAdornment';
import { CustomMuiThemeProvider } from 'theme/CustomMuiThemeProvider';
import { AddOperatorModal } from './AddOperatorModal';
import { EditOperatorModal } from './EditOperatorModal';
import { ImportOperatorModal } from './ImportOperatorModal';
import { UpdatePinModal } from './UpdatePinModal';
import { SyncOperatorsModal } from './SyncOperatorsModal';
import CustomStyledDataGrid from 'components/MuiDataGrid/CustomStyledDataGrid';
import { type GridColDef } from '@mui/x-data-grid';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';
import {
  operatorSkillLabelKey,
  operatorExperienceLabelKey,
} from './operatorImporterUtils';
import OperatorAPI from 'api/operator.api';
import { selectAssociatedDevices } from 'store/asset.slice';
import SyncOutlinedIcon from '@mui/icons-material/SyncOutlined';

export interface OperatorRecord {
  id: string;
  operatorName: string;
  firstName: string;
  lastName: string;
  role: string;
  isActive: boolean;
  pin: string;
  assetVin: string;
  assetBumperNumber: string;
  assetId: string | null;
  skills: string[];
  experience?: string;
}

const Ring = styled(Box, {
  shouldForwardProp: (p) => p !== 'delay' && p !== 'sz',
})<{ delay: number; sz: number }>(({ theme, delay, sz }) => ({
  position: 'absolute',
  width: sz,
  height: sz,
  borderRadius: '50%',
  border: `1px solid ${alpha(theme.palette.primary.main, 0.4)}`,
  '@keyframes radar': {
    '0%': { transform: 'scale(0.8)', opacity: 0.5 },
    '80%': { opacity: 0.08 },
    '100%': { transform: 'scale(2.6)', opacity: 0 },
  },
  animation: 'radar 3s ease-out infinite',
  animationDelay: `${delay}s`,
  pointerEvents: 'none',
}));

const API_BASE = `${process.env.REACT_APP_MANAGEMENT_API_URL ?? ''}/api`;

interface BulkCreateResult {
  created: number;
  failed: number;
  failedOperators?: Array<{ operatorName: string; reason: string }>;
  message?: string;
}

const OperatorImporterContent = (): JSX.Element => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const { accessToken, customer } = useAppSelector((s) => s.authReducer);
  const customerId = customer.id;
  const operatorApi = useRef(new OperatorAPI()).current;
  const associatedDevices = useAppSelector(selectAssociatedDevices);

  const [operators, setOperators] = useState<OperatorRecord[]>([]);
  const [addOpen, setAddOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [editOperator, setEditOperator] = useState<OperatorRecord | null>(null);
  const [loading, setLoading] = useState(false);
  const [updatePinOperator, setUpdatePinOperator] =
    useState<OperatorRecord | null>(null);
  const [allocateOperator, setAllocateOperator] =
    useState<OperatorRecord | null>(null);
  const [bumperInput, setBumperInput] = useState('');
  const [allocating, setAllocating] = useState(false);
  const [loginOperator, setLoginOperator] = useState<OperatorRecord | null>(
    null
  );
  const [loginBumperInput, setLoginBumperInput] = useState('');
  const [loggingIn, setLoggingIn] = useState(false);
  const [loggingInId, setLoggingInId] = useState<string | null>(null);
  const [assetOptions, setAssetOptions] = useState<
    Array<{ id: string; bumperNumber: string; make: string; model: string }>
  >([]);
  const [assetsLoading, setAssetsLoading] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [syncOpen, setSyncOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const PAGE_SIZE = 25;
  const [page, setPage] = useState(0);
  const [colAnchor, setColAnchor] = useState<HTMLElement | null>(null);
  const [colVisibility, setColVisibility] = useState<Record<string, boolean>>({
    operatorName: true,
    role: false,
    skills: true,
    pin: false,
    isActive: true,
    assetVin: false,
    assetBumperNumber: true,
  });

  const fetchOperators = useCallback(async () => {
    if (!accessToken || !customerId) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/operator?customerId=${customerId}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) throw new Error(`Failed to fetch operators: ${res.status}`);
      const data: Array<Record<string, unknown>> = await res.json();
      setOperators(
        data.map((r) => ({
          id: (r.operatorId ?? r.id) as string,
          operatorName: (r.operatorName as string) ?? '',
          firstName: (r.firstName as string)
            ? (r.firstName as string)
            : ((r.operatorName as string) ?? '').split(' ')[0],
          lastName: (r.lastName as string)
            ? (r.lastName as string)
            : ((r.operatorName as string) ?? '').split(' ').slice(1).join(' '),
          role: (r.role as string) ?? '',
          isActive: Boolean(r.isActive),
          pin: (r.pin as string) ?? '',
          assetVin: (r.assetVin as string) ?? '',
          assetBumperNumber: (r.assetBumperNumber as string) ?? '',
          assetId: (r.assetId as string | null) ?? null,
          skills: Array.isArray(r.skills) ? (r.skills as string[]) : [],
          experience: (r.experience as string) ?? undefined,
        }))
      );
    } catch (e) {
      console.error(e);
      dispatch(
        showNotification({
          message: t('operator_importer_fetch_failed'),
          type: 'error',
        })
      );
    } finally {
      setLoading(false);
    }
  }, [accessToken, customerId, dispatch, t]);

  useEffect(() => {
    void fetchOperators();
  }, [fetchOperators]);

  const toast = (message: string, type: 'success' | 'error') => {
    dispatch(showNotification({ message, type }));
  };

  const handleAdd = async (op: OperatorRecord) => {
    try {
      const res = await fetch(`${API_BASE}/operator`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          firstName: op.firstName,
          lastName: op.lastName,
          customerId,
          isActive: op.isActive,
          role: op.role !== '' ? op.role : undefined,
          assetId:
            op.assetId !== null && op.assetId !== '' ? op.assetId : undefined,
          skills: op.skills.length > 0 ? op.skills : undefined,
          experience: op.experience ?? undefined,
        }),
      });
      if (!res.ok) {
        let msg = t('operator_importer_toast_add_failed');
        try {
          const body: unknown = await res.json();
          if (
            body &&
            typeof body === 'object' &&
            'message' in body &&
            typeof (body as Record<string, unknown>).message === 'string'
          ) {
            msg = (body as Record<string, string>).message;
          }
        } catch {}
        throw new Error(msg);
      }
      await fetchOperators();
      setAddOpen(false);
      toast(t('operator_importer_toast_added'), 'success');
    } catch (e) {
      toast(
        e instanceof Error
          ? e.message
          : t('operator_importer_toast_add_failed'),
        'error'
      );
    }
  };

  const handleImport = async (rows: OperatorRecord[]) => {
    try {
      const res = await fetch(`${API_BASE}/operator/bulk`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          customerId,
          operators: rows.map((r) => ({
            firstName: r.firstName,
            lastName: r.lastName,
            isActive: r.isActive,
            role: r.role !== '' ? r.role : undefined,
            assetId:
              r.assetId !== null && r.assetId !== '' ? r.assetId : undefined,
            skills: r.skills.length > 0 ? r.skills : undefined,
            experience: r.experience ?? undefined,
          })),
        }),
      });
      if (!res.ok) throw new Error(`Import failed: ${res.status}`);

      const result = (await res.json()) as BulkCreateResult;
      await fetchOperators();
      setImportOpen(false);

      if (result.failedOperators && result.failedOperators.length > 0) {
        toast(
          t('operator_importer_toast_partial', {
            created: result.created,
            failed: result.failedOperators.length,
          }),
          result.created > 0 ? 'success' : 'error'
        );
      } else {
        toast(
          t('operator_importer_toast_imported', {
            count: result.created ?? rows.length,
          }),
          'success'
        );
      }
    } catch (e) {
      toast(
        e instanceof Error
          ? e.message
          : t('operator_importer_toast_import_failed'),
        'error'
      );
    }
  };

  const readResponseMsg = async (res: Response): Promise<string> => {
    try {
      const body: unknown = await res.json();
      if (
        body &&
        typeof body === 'object' &&
        'message' in body &&
        typeof (body as Record<string, unknown>).message === 'string'
      ) {
        const palette = clientThemePalette[CLIENT_THEME].light;
        return (body as Record<string, string>).message;
      }
    } catch {}
    return '';
  };

  const handleUpdate = async (
    updated: Omit<
      OperatorRecord,
      'id' | 'pin' | 'assetVin' | 'assetBumperNumber'
    >
  ) => {
    if (!editOperator) return;
    try {
      const res = await fetch(`${API_BASE}/operator/${editOperator.id}`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          firstName: updated.firstName,
          lastName: updated.lastName,
          customerId,
          isActive: updated.isActive,
          ...(editOperator.pin ? { pin: parseInt(editOperator.pin, 10) } : {}),
          role: updated.role !== '' ? updated.role : undefined,
          assetId:
            updated.assetId !== null && updated.assetId !== ''
              ? updated.assetId
              : undefined,
          skills: updated.skills.length > 0 ? updated.skills : undefined,
          experience: updated.experience ?? undefined,
        }),
      });
      const msg = await readResponseMsg(res);
      if (!res.ok) throw new Error(msg || `Update failed: ${res.status}`);
      await fetchOperators();
      setEditOperator(null);
      toast(msg || t('operator_importer_toast_updated'), 'success');
    } catch (e) {
      toast(
        e instanceof Error
          ? e.message
          : t('operator_importer_toast_update_failed'),
        'error'
      );
      throw e;
    }
  };

  const handleToggleActive = useCallback(
    async (op: OperatorRecord) => {
      try {
        const res = await fetch(
          `${API_BASE}/operator/${op.id}/activationOperator`,
          {
            method: 'PATCH',
            headers: {
              Authorization: `Bearer ${accessToken}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ isActive: op.isActive }),
          }
        );
        if (!res.ok) throw new Error(`Status update failed: ${res.status}`);
        await fetchOperators();
        toast(
          op.isActive
            ? t('operator_importer_toast_deactivated')
            : t('operator_importer_toast_activated'),
          'success'
        );
      } catch (e) {
        toast(
          e instanceof Error
            ? e.message
            : t('operator_importer_toast_toggle_failed'),
          'error'
        );
      }
    },
    [accessToken, fetchOperators, t]
  );

  const handleUpdatePins = async (
    data: Array<{ name: string; role: string | null; pin: number }>
  ) => {
    if (!updatePinOperator) return;
    const item = data[0];
    try {
      const res = await fetch(`${API_BASE}/operator/${updatePinOperator.id}`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          firstName: updatePinOperator.firstName,
          lastName: updatePinOperator.lastName,
          customerId,
          isActive: updatePinOperator.isActive,
          pin: item.pin,
          role:
            updatePinOperator.role !== '' ? updatePinOperator.role : undefined,
          assetId:
            updatePinOperator.assetId !== null &&
            updatePinOperator.assetId !== ''
              ? updatePinOperator.assetId
              : undefined,
          skills:
            updatePinOperator.skills.length > 0
              ? updatePinOperator.skills
              : undefined,
          experience: updatePinOperator.experience ?? undefined,
        }),
      });
      const msg = await readResponseMsg(res);
      if (!res.ok) throw new Error(msg || `PIN update failed: ${res.status}`);
      await fetchOperators();
      setUpdatePinOperator(null);
      toast(msg || t('operator_importer_toast_pins_updated'), 'success');
    } catch (e) {
      toast(
        e instanceof Error
          ? e.message
          : t('operator_importer_toast_update_failed'),
        'error'
      );
      throw e;
    }
  };

  useEffect(() => {
    if (!accessToken || !customerId) return;
    setAssetsLoading(true);
    void fetch(`${API_BASE}/asset?customerId=${customerId}`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    })
      .then(async (res) => {
        if (!res.ok) return;
        const data: Array<Record<string, unknown>> = await res.json();
        setAssetOptions(
          data
            .filter((a) => a.bumperNumber)
            .map((a) => ({
              id: String(a.assetId ?? a.id ?? ''),
              bumperNumber: String(a.bumperNumber),
              make: String(a.make ?? ''),
              model: String(a.model ?? ''),
            }))
            .sort((a, b) => a.bumperNumber.localeCompare(b.bumperNumber))
        );
      })
      .finally(() => {
        setAssetsLoading(false);
      });
  }, [accessToken, customerId]);

  useEffect(() => {
    if (!allocateOperator) return;
    setBumperInput('');
  }, [allocateOperator]);

  useEffect(() => {
    if (!loginOperator) return;
    setLoginBumperInput('');
  }, [loginOperator]);

  const handleAllocate = async () => {
    if (!allocateOperator || !bumperInput.trim()) return;
    setAllocating(true);
    try {
      const res = await fetch(
        `${API_BASE}/operator/${allocateOperator.id}/allocate-asset`,
        {
          method: 'PATCH',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            customerId,
            bumperNumber: bumperInput.trim(),
          }),
        }
      );
      const msg = await readResponseMsg(res);
      if (!res.ok) throw new Error(msg || `Allocate failed: ${res.status}`);
      await fetchOperators();
      setAllocateOperator(null);
      setBumperInput('');
      toast(msg || t('operator_importer_toast_allocated'), 'success');
    } catch (e) {
      toast(
        e instanceof Error
          ? e.message
          : t('operator_importer_toast_allocate_failed'),
        'error'
      );
    } finally {
      setAllocating(false);
    }
  };

  const handleSyncOperators = async (deviceId: string | undefined) => {
    setSyncing(true);
    try {
      const result = await operatorApi.syncCredentials(deviceId);
      setSyncOpen(false);
      toast(
        t('operator_importer_toast_sync_success', {
          operatorsCount: result.operatorsCount,
          devicesCount: result.devicesCount,
        }),
        'success'
      );
    } catch (e) {
      toast(
        e instanceof Error
          ? e.message
          : t('operator_importer_toast_sync_failed'),
        'error'
      );
    } finally {
      setSyncing(false);
    }
  };

  const submitLogin = async (operatorId: string, bumperNumber: string) => {
    try {
      await operatorApi.sessionSwitch(bumperNumber, operatorId);
      toast(t('operator_importer_toast_login_success'), 'success');
    } catch (e) {
      const backendMessage = axios.isAxiosError(e)
        ? (e.response?.data?.message as string | undefined)
        : undefined;
      toast(
        backendMessage ?? t('operator_importer_toast_login_failed'),
        'error'
      );
    }
  };

  const handleLogin = async () => {
    if (!loginOperator || !loginBumperInput.trim()) return;
    setLoggingIn(true);
    try {
      await submitLogin(loginOperator.id, loginBumperInput.trim());
      setLoginOperator(null);
      setLoginBumperInput('');
    } finally {
      setLoggingIn(false);
    }
  };

  const handleDirectLogin = async (op: OperatorRecord) => {
    setLoggingInId(op.id);
    try {
      await submitLogin(op.id, op.assetBumperNumber);
    } finally {
      setLoggingInId(null);
    }
  };

  const handleDisallocate = async (op: OperatorRecord) => {
    try {
      const res = await fetch(
        `${API_BASE}/operator/${op.id}/disallocate-asset`,
        {
          method: 'PATCH',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ customerId }),
        }
      );
      const msg = await readResponseMsg(res);
      if (!res.ok) throw new Error(msg || `Disallocate failed: ${res.status}`);
      await fetchOperators();
      toast(msg || t('operator_importer_toast_disallocated'), 'success');
    } catch (e) {
      toast(
        e instanceof Error
          ? e.message
          : t('operator_importer_toast_disallocate_failed'),
        'error'
      );
    }
  };

  const exportExcel = (includePin: boolean) => {
    const rows = operators.map((o) => ({
      [t('operator_importer_col_operator_name')]: o.operatorName,
      [t('operator_importer_col_role')]: o.role !== '' ? o.role : '—',
      [t('operator_importer_field_skills')]:
        o.skills && o.skills.length > 0
          ? o.skills
              .map((skill) =>
                t(operatorSkillLabelKey(skill), { defaultValue: skill })
              )
              .join(', ')
          : '—',
      [t('operator_importer_field_experience')]: o.experience
        ? t(operatorExperienceLabelKey(o.experience), {
            defaultValue: o.experience,
          })
        : '—',
      [t('operator_importer_col_pin')]: includePin
        ? String(o.pin ?? '') !== ''
          ? String(o.pin)
          : '—'
        : '*******',
      [t('operator_importer_field_active')]: o.isActive ? t('yes') : t('no'),
      [t('operator_importer_col_asset_bumper')]:
        o.assetBumperNumber !== '' ? o.assetBumperNumber : '—',
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Operators');
    const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(
      new Blob([buf], { type: 'application/octet-stream' }),
      `operators_${Date.now()}.xlsx`
    );
    setExportDialogOpen(false);
  };

  const hasData = operators.length > 0;
  const activeCount = operators.filter((o) => o.isActive).length;

  const filteredOperators = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return operators;
    return operators.filter((o) => {
      const skillsStr = o.skills?.join(' ').toLowerCase() ?? '';
      return (
        String(o.operatorName ?? '')
          .toLowerCase()
          .includes(q) ||
        String(o.role ?? '')
          .toLowerCase()
          .includes(q) ||
        skillsStr.includes(q) ||
        String(o.experience ?? '')
          .toLowerCase()
          .includes(q) ||
        String(o.pin ?? '')
          .toLowerCase()
          .includes(q) ||
        String(o.assetBumperNumber ?? '')
          .toLowerCase()
          .includes(q)
      );
    });
  }, [operators, searchQuery]);

  // reset to page 0 whenever search changes
  useEffect(() => {
    setPage(0);
  }, [searchQuery]);

  const pageCount = Math.max(
    1,
    Math.ceil(filteredOperators.length / PAGE_SIZE)
  );
  const safePage = Math.min(page, pageCount - 1);
  const pagedOperators = filteredOperators.slice(
    safePage * PAGE_SIZE,
    safePage * PAGE_SIZE + PAGE_SIZE
  );
  const hasResults = filteredOperators.length > 0;

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'operatorName',
        headerName: t('operator_importer_col_operator_name'),
        flex: 2,
        minWidth: 160,
        valueFormatter: ({ value }: { value: unknown }) =>
          value ? String(value) : '—',
      },
      {
        field: 'role',
        headerName: t('operator_importer_col_role'),
        flex: 1.2,
        minWidth: 120,
        valueFormatter: ({ value }: { value: unknown }) =>
          value ? String(value) : '—',
      },
      {
        field: 'skills',
        headerName: t('operator_importer_field_skills'),
        flex: 2,
        minWidth: 160,
        valueFormatter: ({ value }: { value: unknown }) =>
          Array.isArray(value) && value.length > 0
            ? (value as string[])
                .map((skill) =>
                  t(operatorSkillLabelKey(skill), { defaultValue: skill })
                )
                .join(', ')
            : '—',
      },
      {
        field: 'experience',
        headerName: t('operator_importer_field_experience'),
        flex: 1.2,
        minWidth: 130,
        valueFormatter: ({ value }: { value: unknown }) => {
          if (!value) return '—';
          return t(operatorExperienceLabelKey(String(value)), {
            defaultValue: String(value),
          });
        },
      },
      {
        field: 'pin',
        headerName: t('operator_importer_col_pin'),
        width: 110,
        valueFormatter: ({ value }: { value: unknown }) =>
          value ? String(value) : '—',
      },
      {
        field: 'isActive',
        headerName: t('operator_importer_col_active'),
        width: 110,
        renderCell: (params) => (
          <Chip
            label={
              params.value
                ? t('operator_importer_chip_yes')
                : t('operator_importer_chip_no')
            }
            size="small"
            color={params.value ? 'success' : 'default'}
            sx={{ fontWeight: 600, fontSize: '0.72rem' }}
          />
        ),
      },
      {
        field: 'assetVin',
        headerName: t('operator_importer_col_asset_vin'),
        flex: 1.5,
        minWidth: 130,
        valueFormatter: ({ value }: { value: unknown }) =>
          value ? String(value) : '—',
      },
      {
        field: 'assetBumperNumber',
        headerName: t('operator_importer_col_asset'),
        flex: 1.2,
        minWidth: 120,
        valueFormatter: ({ value }: { value: unknown }) =>
          value ? String(value) : '—',
      },
      {
        field: 'actions',
        headerName: t('operator_importer_col_actions'),
        width: 120,
        sortable: false,
        disableColumnMenu: true,
        disableReorder: true,
        renderCell: (params) => {
          const row = params.row as OperatorRecord;
          return (
            <Box
              sx={{
                display: 'flex',
                gap: 0.5,
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
              }}
            >
              <Tooltip title={t('operator_importer_edit')}>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setEditOperator(row);
                  }}
                  sx={{
                    color: 'text.secondary',
                    '&:hover': { color: 'primary.main' },
                  }}
                >
                  <EditOutlinedIcon sx={{ fontSize: 17 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title={t('operator_importer_btn_update_pins')}>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setUpdatePinOperator(row);
                  }}
                  sx={{
                    color: 'text.secondary',
                    '&:hover': { color: 'warning.main' },
                  }}
                >
                  <VpnKeyOutlinedIcon sx={{ fontSize: 17 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title={t('operator_importer_btn_login')}>
                <span>
                  <IconButton
                    size="small"
                    disabled={loggingInId === row.id}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (row.assetBumperNumber) {
                        void handleDirectLogin(row);
                      } else {
                        setLoginBumperInput('');
                        setLoginOperator(row);
                      }
                    }}
                    sx={{
                      color: 'text.secondary',
                      '&:hover': { color: 'primary.main' },
                    }}
                  >
                    {loggingInId === row.id ? (
                      <CircularProgress size={15} />
                    ) : (
                      <LoginOutlinedIcon sx={{ fontSize: 17 }} />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
              {row.assetBumperNumber ? (
                <Tooltip title={t('operator_importer_btn_disallocate')}>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      void handleDisallocate(row);
                    }}
                    sx={{
                      color: 'text.secondary',
                      '&:hover': { color: 'error.main' },
                    }}
                  >
                    <LinkOffOutlinedIcon sx={{ fontSize: 17 }} />
                  </IconButton>
                </Tooltip>
              ) : (
                <Tooltip title={t('operator_importer_btn_allocate')}>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setBumperInput('');
                      setAllocateOperator(row);
                    }}
                    sx={{
                      color: 'text.secondary',
                      '&:hover': { color: 'success.main' },
                    }}
                  >
                    <AddLinkOutlinedIcon sx={{ fontSize: 17 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          );
        },
      },
    ],
    [handleDisallocate, t]
  );

  return (
    <Box
      sx={{
        px: '32px',
        pt: '20px',
        pb: '20px',
        mx: -4,
        height: 'calc(100vh - 130px)',
        minHeight: 500,
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
      }}
    >
      {/* ── HEADER ── */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          mb: 3,
        }}
      >
        <Box>
          <Typography
            variant="h5"
            fontWeight={700}
            color="text.primary"
            lineHeight={1.2}
          >
            {t('operator_importer_title')}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 0.4, mb: 1.5 }}
          >
            {t('operator_importer_subtitle')}
          </Typography>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.6,
                px: 1.4,
                py: 0.35,
                borderRadius: '6px',
                bgcolor: (t) => alpha(t.palette.primary.main, 0.1),
                border: '1px solid',
                borderColor: (t) => alpha(t.palette.primary.main, 0.22),
              }}
            >
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  bgcolor: 'primary.main',
                }}
              />
              <Typography
                variant="caption"
                fontWeight={600}
                color="text.secondary"
              >
                {operators.length} {t('operator_importer_total')}
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.6,
                px: 1.4,
                py: 0.35,
                borderRadius: '6px',
                bgcolor: (t) => alpha(t.palette.success.main, 0.1),
                border: '1px solid',
                borderColor: (t) => alpha(t.palette.success.main, 0.22),
              }}
            >
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  bgcolor: 'success.main',
                }}
              />
              <Typography
                variant="caption"
                fontWeight={600}
                color="text.secondary"
              >
                {activeCount} {t('operator_importer_active')}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            gap: 1.5,
            flexShrink: 0,
            mt: 0.5,
            alignItems: 'center',
          }}
        >
          <Tooltip title={t('operator_importer_btn_refresh')}>
            <span>
              <IconButton
                onClick={() => {
                  void fetchOperators();
                }}
                disabled={loading}
                size="small"
                sx={{
                  border: '1px solid',
                  borderColor: (t) => alpha(t.palette.divider, 0.6),
                  borderRadius: '8px',
                  p: 0.8,
                }}
              >
                <RefreshIcon
                  fontSize="small"
                  sx={{
                    transition: 'transform 0.5s',
                    transform: loading ? 'rotate(360deg)' : 'none',
                  }}
                />
              </IconButton>
            </span>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setAddOpen(true);
            }}
            sx={{ px: 2.5, fontWeight: 600, borderRadius: '8px' }}
          >
            {t('operator_importer_btn_add')}
          </Button>
          <Button
            variant="contained"
            startIcon={<CloudUploadIcon />}
            onClick={() => {
              setImportOpen(true);
            }}
            sx={{ px: 2.5, fontWeight: 600, borderRadius: '8px' }}
          >
            {t('operator_importer_btn_import')}
          </Button>
          <Button
            variant="contained"
            startIcon={<DownloadOutlinedIcon />}
            onClick={() => {
              setExportDialogOpen(true);
            }}
            disabled={!hasData}
            sx={{ px: 2.5, fontWeight: 600, borderRadius: '8px' }}
          >
            {t('operator_importer_btn_export')}
          </Button>
          <Button
            variant="contained"
            startIcon={<SyncOutlinedIcon />}
            onClick={() => {
              setSyncOpen(true);
            }}
            disabled={!hasData}
            sx={{ px: 2.5, fontWeight: 600, borderRadius: '8px' }}
          >
            {t('operator_importer_btn_sync_devices')}
          </Button>
        </Box>
      </Box>

      {/* ── BODY ── */}
      <Box
        sx={{
          flex: 1,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {loading && operators.length === 0 ? (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CircularProgress size={40} />
          </Box>
        ) : !hasData ? (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
              overflow: 'hidden',
              backgroundImage: `radial-gradient(circle, ${alpha(
                theme.palette.primary.main,
                0.1
              )} 1px, transparent 1px)`,
              backgroundSize: '28px 28px',
              borderRadius: 3,
              border: '1px solid',
              borderColor: (t) => alpha(t.palette.divider, 0.5),
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                background: (t) =>
                  `radial-gradient(ellipse 65% 65% at 50% 50%, transparent 25%, ${t.palette.background.default} 100%)`,
                pointerEvents: 'none',
              }}
            />
            <Box
              sx={{
                position: 'relative',
                width: 96,
                height: 96,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 3.5,
                zIndex: 1,
              }}
            >
              <Ring delay={0} sz={96} />
              <Ring delay={1} sz={96} />
              <Ring delay={2} sz={96} />
              <Box
                sx={{
                  width: 78,
                  height: 78,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: (t) => alpha(t.palette.primary.main, 0.12),
                  border: '2px solid',
                  borderColor: (t) => alpha(t.palette.primary.main, 0.35),
                  boxShadow: `0 0 40px ${alpha(
                    theme.palette.primary.main,
                    0.25
                  )}`,
                  zIndex: 1,
                  position: 'relative',
                }}
              >
                <PersonOutlinedIcon
                  sx={{ fontSize: 40, color: 'primary.main' }}
                />
              </Box>
            </Box>
            <Typography
              variant="h5"
              fontWeight={700}
              color="text.primary"
              textAlign="center"
              sx={{ zIndex: 1 }}
            >
              {t('operator_importer_empty_title')}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              textAlign="center"
              sx={{ mt: 1, mb: 4, maxWidth: 360, lineHeight: 1.8, zIndex: 1 }}
            >
              {t('operator_importer_empty_body')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, zIndex: 1 }}>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => {
                  setAddOpen(true);
                }}
                sx={{ px: 3.5, py: 1.1, fontWeight: 600, borderRadius: '8px' }}
              >
                {t('operator_importer_btn_add')}
              </Button>
              <Button
                variant="contained"
                startIcon={<CloudUploadIcon />}
                onClick={() => {
                  setImportOpen(true);
                }}
                sx={{ px: 3.5, py: 1.1, fontWeight: 600, borderRadius: '8px' }}
              >
                {t('operator_importer_btn_import')}
              </Button>
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 1,
              }}
            >
              <Typography
                variant="body2"
                color="text.secondary"
                fontWeight={500}
              >
                {t('operator_importer_all_operators')}{' '}
                <Typography
                  component="span"
                  variant="body2"
                  color="primary.main"
                  fontWeight={700}
                >
                  (
                  {searchQuery.trim()
                    ? `${filteredOperators.length} / ${operators.length}`
                    : operators.length}
                  )
                </Typography>
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <TextField
                  size="small"
                  placeholder={t('operator_importer_search_placeholder')}
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon
                          sx={{ fontSize: 16, color: 'text.secondary' }}
                        />
                      </InputAdornment>
                    ),
                    endAdornment: searchQuery ? (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSearchQuery('');
                          }}
                          edge="end"
                          sx={{ p: 0.25 }}
                        >
                          <ClearIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                  }}
                  sx={{
                    width: 220,
                    mr: 0.5,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                      fontSize: '0.82rem',
                    },
                  }}
                />
                <Tooltip
                  title={
                    colVisibility.pin
                      ? t('operator_importer_pin_hide')
                      : t('operator_importer_pin_show')
                  }
                >
                  <IconButton
                    size="small"
                    onClick={() => {
                      setColVisibility((prev) => ({
                        ...prev,
                        pin: !prev.pin,
                      }));
                    }}
                    sx={{
                      border: '1px solid',
                      borderColor: (th) => alpha(th.palette.divider, 0.6),
                      borderRadius: '6px',
                      p: 0.6,
                      color: colVisibility.pin
                        ? 'primary.main'
                        : 'text.secondary',
                    }}
                  >
                    {colVisibility.pin ? (
                      <VisibilityOutlinedIcon sx={{ fontSize: 15 }} />
                    ) : (
                      <VisibilityOffOutlinedIcon sx={{ fontSize: 15 }} />
                    )}
                  </IconButton>
                </Tooltip>
                <Button
                  size="small"
                  startIcon={<FilterListIcon sx={{ fontSize: 15 }} />}
                  onClick={(e) => {
                    setColAnchor(e.currentTarget);
                  }}
                  sx={{
                    color: 'text.secondary',
                    fontSize: '0.76rem',
                    borderRadius: '6px',
                  }}
                >
                  {t('operator_importer_filters')}
                </Button>
              </Box>
              <Popover
                open={Boolean(colAnchor)}
                anchorEl={colAnchor}
                onClose={() => {
                  setColAnchor(null);
                }}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                PaperProps={{
                  sx: {
                    mt: 0.5,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: (th: Theme) => alpha(th.palette.divider, 0.5),
                    p: 1.5,
                    minWidth: 200,
                  },
                }}
              >
                <Typography
                  variant="caption"
                  fontWeight={700}
                  color="text.secondary"
                  sx={{
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                    px: 0.5,
                    mb: 0.5,
                    display: 'block',
                  }}
                >
                  {t('operator_importer_col_visibility')}
                </Typography>
                {Object.keys(colVisibility).map((col) => (
                  <Box key={col} sx={{ display: 'block' }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={colVisibility[col]}
                          onChange={(e) => {
                            setColVisibility((prev) => ({
                              ...prev,
                              [col]: e.target.checked,
                            }));
                          }}
                        />
                      }
                      label={
                        <Typography
                          variant="body2"
                          sx={{ fontSize: '0.82rem' }}
                        >
                          {col === 'skills'
                            ? t('operator_importer_field_skills')
                            : t(
                                `operator_importer_col_${
                                  col === 'operatorName'
                                    ? 'operator_name'
                                    : col === 'isActive'
                                    ? 'active'
                                    : col === 'assetVin'
                                    ? 'asset_vin'
                                    : col === 'assetBumperNumber'
                                    ? 'asset'
                                    : col
                                }`
                              )}
                        </Typography>
                      }
                      sx={{ mx: 0, width: '100%' }}
                    />
                  </Box>
                ))}
              </Popover>
            </Box>
            {hasResults ? (
              <Box sx={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
                {loading && (
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: (t) => alpha(t.palette.background.paper, 0.6),
                      zIndex: 10,
                      borderRadius: 2,
                    }}
                  >
                    <CircularProgress size={32} />
                  </Box>
                )}
                <CustomStyledDataGrid
                  rows={pagedOperators}
                  columns={columns}
                  getRowId={(row) => row.id}
                  disableRowSelectionOnClick
                  hideFooter
                  columnVisibilityModel={colVisibility}
                />
              </Box>
            ) : (
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                }}
              >
                <SearchIcon sx={{ fontSize: 40, color: 'text.disabled' }} />
                <Typography variant="body2" color="text.secondary">
                  {t('operator_importer_no_results')}
                </Typography>
              </Box>
            )}

            {hasResults && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1.5,
                  py: 1.5,
                  borderTop: '1px solid',
                  borderColor: (th) => alpha(th.palette.divider, 0.4),
                  flexShrink: 0,
                }}
              >
                <IconButton
                  size="small"
                  onClick={() => {
                    setPage((p) => Math.max(0, p - 1));
                  }}
                  disabled={safePage === 0}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: '8px',
                    p: 0.5,
                  }}
                >
                  <ChevronLeftIcon sx={{ fontSize: 18 }} />
                </IconButton>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ minWidth: 80, textAlign: 'center' }}
                >
                  {safePage + 1} / {pageCount}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => {
                    setPage((p) => Math.min(pageCount - 1, p + 1));
                  }}
                  disabled={safePage >= pageCount - 1}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: '8px',
                    p: 0.5,
                  }}
                >
                  <ChevronRightIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Box>
            )}
          </Box>
        )}
      </Box>

      <AddOperatorModal
        open={addOpen}
        onClose={() => {
          setAddOpen(false);
        }}
        onAdd={(op) => {
          void handleAdd(op);
        }}
        assetOptions={assetOptions}
      />
      <EditOperatorModal
        open={editOperator !== null}
        operator={editOperator}
        onClose={() => {
          setEditOperator(null);
        }}
        onSave={async (updated) => {
          await handleUpdate(updated);
        }}
        assetOptions={assetOptions}
      />
      <ImportOperatorModal
        open={importOpen}
        onClose={() => {
          setImportOpen(false);
        }}
        onImport={async (rows) => {
          await handleImport(rows);
        }}
      />
      <UpdatePinModal
        open={updatePinOperator !== null}
        operators={updatePinOperator ? [updatePinOperator] : []}
        onClose={() => {
          setUpdatePinOperator(null);
        }}
        onSave={async (data) => {
          await handleUpdatePins(data);
        }}
      />
      <SyncOperatorsModal
        open={syncOpen}
        devices={associatedDevices}
        syncing={syncing}
        onClose={() => {
          setSyncOpen(false);
        }}
        onSync={(deviceId) => {
          void handleSyncOperators(deviceId);
        }}
      />

      {/* Allocate asset dialog */}
      <Dialog
        open={allocateOperator !== null}
        onClose={() => {
          if (!allocating) setAllocateOperator(null);
        }}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: (th) => alpha(th.palette.primary.main, 0.15),
            backgroundImage: 'none',
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
          {t('operator_importer_allocate_title')}
          <Typography variant="caption" display="block" color="text.secondary">
            {allocateOperator?.operatorName}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Autocomplete
            options={assetOptions.map((a) => a.bumperNumber)}
            value={bumperInput || null}
            onChange={(_, val) => {
              setBumperInput(val ?? '');
            }}
            loading={assetsLoading}
            size="small"
            sx={{ mt: 1 }}
            renderInput={(params) => (
              <TextField
                {...params}
                autoFocus
                label={t('operator_importer_allocate_bumper_label')}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {assetsLoading ? <CircularProgress size={14} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            variant="outlined"
            color="inherit"
            onClick={() => {
              setAllocateOperator(null);
            }}
            disabled={allocating}
            sx={{ borderRadius: '8px', fontWeight: 600 }}
          >
            {t('operator_importer_cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              void handleAllocate();
            }}
            disabled={allocating || !bumperInput.trim()}
            startIcon={
              allocating ? (
                <CircularProgress size={14} color="inherit" />
              ) : undefined
            }
            sx={{ borderRadius: '8px', fontWeight: 600 }}
          >
            {t('operator_importer_btn_allocate')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Login (session switch) dialog */}
      <Dialog
        open={loginOperator !== null}
        onClose={() => {
          if (!loggingIn) setLoginOperator(null);
        }}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: (th) => alpha(th.palette.primary.main, 0.15),
            backgroundImage: 'none',
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
          {t('operator_importer_login_title')}
          <Typography variant="caption" display="block" color="text.secondary">
            {loginOperator?.operatorName}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Autocomplete
            options={assetOptions.map((a) => a.bumperNumber)}
            value={loginBumperInput || null}
            onChange={(_, val) => {
              setLoginBumperInput(val ?? '');
            }}
            loading={assetsLoading}
            size="small"
            sx={{ mt: 1 }}
            renderInput={(params) => (
              <TextField
                {...params}
                autoFocus
                label={t('operator_importer_login_bumper_label')}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {assetsLoading ? <CircularProgress size={14} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            variant="outlined"
            color="inherit"
            onClick={() => {
              setLoginOperator(null);
            }}
            disabled={loggingIn}
            sx={{ borderRadius: '8px', fontWeight: 600 }}
          >
            {t('operator_importer_cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              void handleLogin();
            }}
            disabled={loggingIn || !loginBumperInput.trim()}
            startIcon={
              loggingIn ? (
                <CircularProgress size={14} color="inherit" />
              ) : undefined
            }
            sx={{ borderRadius: '8px', fontWeight: 600 }}
          >
            {t('operator_importer_btn_login')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Export confirmation dialog */}
      <Dialog
        open={exportDialogOpen}
        onClose={() => {
          setExportDialogOpen(false);
        }}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: (th) => alpha(th.palette.primary.main, 0.15),
            backgroundImage: 'none',
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
          {t('operator_importer_export_title')}
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {t('operator_importer_export_body')}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            variant="outlined"
            color="inherit"
            onClick={() => {
              exportExcel(false);
            }}
            sx={{ borderRadius: '8px', fontWeight: 600 }}
          >
            {t('operator_importer_export_without_pin')}
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              exportExcel(true);
            }}
            sx={{ borderRadius: '8px', fontWeight: 600 }}
          >
            {t('operator_importer_export_with_pin')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export const OperatorImporterView = (): JSX.Element => (
  <CustomMuiThemeProvider>
    <OperatorImporterContent />
  </CustomMuiThemeProvider>
);
